import {
  ILocationIndication,
  LocationActions,
  LocationAirConditioning,
  LocationChargeStatus,
  LocationFeeOption,
  LocationFurnished,
  LocationJourneyIntent,
  LocationPlanAddon,
  LocationPlanPercentage,
  LocationPropertySituation,
  LocationSendChannel,
  LocationSignatureChannel,
  LocationSituation,
  LocationSituationTone,
  LocationTrackItem,
  LocationTrackKey,
  LocationTrackState,
} from '@/services/Partner/types';

import { typeOptions } from '@/pages/Partners/pages/Indicate/Form/utils';
import { EnumType, partnersRoutes } from '@/pages/Partners/types/paths';

/**
 * Vocabulário único da jornada digital de locação SZS (wallet do parceiro).
 *
 * Tudo que a landing, o wizard, a lista e o detalhe precisam dizer sobre a
 * esteira, a situação, os planos, as taxas e os canais mora aqui, e só aqui.
 * O back calcula `track`/`situation`/`actions`; este módulo dá nome e cor, e
 * cobre o caso de um registro antigo chegar sem eles.
 */

/* ── Caminhos da locação e rotas da jornada ─────────────────────────── */

/**
 * A porta da locação tem dois caminhos (call de validação com o Ambrosi, 26/08):
 * apresentar o proprietário e deixar a Seazone fechar, ou conduzir o fechamento
 * até o contrato assinado. Tudo que as telas dizem sobre eles sai daqui.
 */
export const LOCATION_ENTRY_PATH = '/parceiros/indicar/locacao';
export const WIZARD_PATH = '/parceiros/indicar/nova';
export const LEGACY_LOCATION_FORM_PATH = '/parceiros/indicar/formulario/locacao';
export const LIST_PATH = `/parceiros/${partnersRoutes.indications.main}/${partnersRoutes.indications.filter.location}`;

export const INTENT_LABEL: Record<LocationJourneyIntent, string> = {
  indication: 'Indicação',
  contract: 'Fechamento de contrato',
};

/** `?fluxo=indicacao` na URL do wizard; qualquer outra coisa é fechamento. */
export const INTENT_PARAM = 'fluxo';
const INTENT_BY_PARAM: Record<string, LocationJourneyIntent> = {
  indicacao: 'indication',
  contrato: 'contract',
};
const PARAM_BY_INTENT: Record<LocationJourneyIntent, string> = {
  indication: 'indicacao',
  contract: 'contrato',
};

export const intentFromParam = (value?: string | null): LocationJourneyIntent =>
  INTENT_BY_PARAM[value ?? ''] ?? 'contract';

/** `/parceiros/indicar/nova?fluxo=indicacao`, com o id do rascunho quando já existe. */
export const wizardPathFor = (intent: LocationJourneyIntent, id?: number | string): string =>
  `${WIZARD_PATH}${id ? `/${id}` : ''}?${INTENT_PARAM}=${PARAM_BY_INTENT[intent]}`;

export const isIndicationOnly = (item: Partial<ILocationIndication>): boolean =>
  item.journey_intent === 'indication';

/* ── Esteira (6 etapas) ─────────────────────────────────────────────── */

export const TRACK_STEPS: { key: LocationTrackKey; label: string }[] = [
  { key: 'indicated', label: 'Indicada' },
  { key: 'sent', label: 'Contrato enviado para assinatura' },
  { key: 'opened', label: 'Abriu' },
  { key: 'paid', label: 'Pagou a taxa' },
  { key: 'signed', label: 'Assinou' },
  { key: 'won', label: 'Ganho' },
];

export const TRACK_LEGEND =
  'Esteira: enviado → abriu → pagou → assinou → ganho. Azul = concluído, contorno = etapa atual, laranja = com a Seazone, vermelho = precisa de atenção.';

export const TRACK_STATE_COLORS: Record<
  LocationTrackState,
  { bg: string; border: string; text: string; ring: string }
> = {
  done: { bg: '#0d4bd0', border: '#0d4bd0', text: '#ffffff', ring: 'transparent' },
  current: { bg: '#ffffff', border: '#0d4bd0', text: '#0d4bd0', ring: '#e8f0fe' },
  warn: { bg: '#FF832B', border: '#FF832B', text: '#ffffff', ring: 'transparent' },
  error: { bg: '#DA1E28', border: '#DA1E28', text: '#ffffff', ring: 'transparent' },
  pending: { bg: '#ffffff', border: '#e6e7ea', text: '#9c9fad', ring: 'transparent' },
};

export const TRACK_STATE_LABEL: Record<LocationTrackState, string> = {
  done: 'concluído',
  current: 'etapa atual',
  warn: 'com a Seazone',
  error: 'precisa de atenção',
  pending: 'pendente',
};

export const TRACK_STATE_GLYPH: Record<LocationTrackState, string> = {
  done: '✓',
  current: '',
  warn: '…',
  error: '!',
  pending: '',
};

/* ── Situação (chip) ────────────────────────────────────────────────── */

export const SITUATION_TONE_COLORS: Record<
  Exclude<LocationSituationTone, ''> | 'default',
  { bg: string; color: string }
> = {
  ok: { bg: '#dcfce7', color: '#15803d' },
  warn: { bg: '#fff1ea', color: '#c2410c' },
  err: { bg: '#fee2e2', color: '#b91c1c' },
  gray: { bg: '#f3f3f5', color: '#616163' },
  default: { bg: '#e8f0fe', color: '#0d4bd0' },
};

export const situationColors = (tone: LocationSituationTone | undefined) =>
  tone && tone in SITUATION_TONE_COLORS
    ? SITUATION_TONE_COLORS[tone as Exclude<LocationSituationTone, ''>]
    : SITUATION_TONE_COLORS.default;

/* ── Proposta: planos, adicionais, taxas, canais ────────────────────── */

export const PLANS: Record<
  LocationPlanPercentage,
  { percentage: LocationPlanPercentage; label: string; description: string }
> = {
  20: {
    percentage: 20,
    label: 'Plano 20%',
    description: 'Gestão completa do imóvel. Sem adicionais.',
  },
  22: {
    percentage: 22,
    label: 'Plano 22%',
    description: 'Gestão completa + um adicional à escolha: gestão de contas ou seguro Easy Cover.',
  },
  23: {
    percentage: 23,
    label: 'Plano 23%',
    description: 'Gestão completa + gestão de contas + seguro Easy Cover.',
  },
};

export const PLAN_ORDER: LocationPlanPercentage[] = [20, 22, 23];

export const PLAN_ADDONS: Record<Exclude<LocationPlanAddon, ''>, string> = {
  gestao_contas: 'Gestão de contas',
  seguro_easy_cover: 'Seguro Easy Cover',
};

export interface LocationFeeDef {
  name: string;
  /** Total cobrado do proprietário, em reais. */
  value: number;
  /** Mesmo texto do ``fee_label`` do back (sapron-backend ``LOCATION_FEE_OPTIONS``). */
  label: string;
  sub: string;
  /** Parcelamento fixo: o card mostra a parcela em destaque no lugar do total. */
  installments?: { count: number; value: number };
}

export const FEES: Record<LocationFeeOption, LocationFeeDef> = {
  pix_avista: {
    name: 'À vista no Pix',
    value: 999,
    label: 'R$ 999 à vista (Pix)',
    sub: 'Pagamento único, confirmação na hora',
  },
  cartao_parcelado: {
    name: 'Parcelado no cartão',
    value: 1200,
    label: 'R$ 1.200 em 3x de R$ 400 (cartão)',
    sub: 'Total R$ 1.200 no cartão de crédito',
    installments: { count: 3, value: 400 },
  },
};

export const FEE_ORDER: LocationFeeOption[] = ['pix_avista', 'cartao_parcelado'];

export const SEND_CHANNELS: Record<LocationSendChannel, string> = {
  whatsapp: 'WhatsApp',
  email: 'E-mail',
  link: 'Copiar link',
};

export const SEND_CHANNEL_ORDER: LocationSendChannel[] = ['whatsapp', 'email', 'link'];

export const SIGNATURE_CHANNELS: Record<LocationSignatureChannel, string> = {
  email: 'E-mail',
  whatsapp: 'WhatsApp',
};

export const SIGNATURE_CHANNEL_ORDER: LocationSignatureChannel[] = ['email', 'whatsapp'];

export const DEFAULT_NATIONALITY = 'Brasileira';

/* ── Qualificação do imóvel ─────────────────────────────────────────── */

/**
 * As mesmas três perguntas da LP de novos proprietários, com os mesmos textos.
 *
 * O SLA é do SAI e casa por texto exato (`sai.szs_formularios`, linha "[MQL] SLAs para a LP de
 * novos proprietários"), então esta lista é vocabulário, não opinião: mudar uma palavra aqui
 * derruba a comparação lá. Quem decide o que passa é a configuração, não o wizard.
 */
export const FURNISHED_OPTIONS: readonly LocationFurnished[] = ['Sim', 'Não', 'Parcialmente'];

export const AIR_CONDITIONING_OPTIONS: readonly LocationAirConditioning[] = [
  'Sim',
  'Não',
  'Não, mas estou disposto a instalar',
];

export const PROPERTY_SITUATION_OPTIONS: readonly LocationPropertySituation[] = [
  'Disponível para alugar agora',
  'Já alugo por temporada',
  'Alugado por contrato anual',
  'Em reforma / pronto em breve',
  'Uso próprio (ocioso parte do ano)',
];

export const CHARGE_STATUS: Record<
  Exclude<LocationChargeStatus, ''> | 'none',
  { text: string; tone: LocationSituationTone }
> = {
  pending: { text: 'aguardando pagamento', tone: 'warn' },
  released: { text: 'cobrança liberada', tone: '' },
  paid: { text: 'paga', tone: 'ok' },
  exempt: { text: 'isenta', tone: 'ok' },
  unavailable: { text: 'cobrança indisponível', tone: 'err' },
  none: { text: 'sem cobrança', tone: 'gray' },
};

export const chargeStatus = (status?: LocationChargeStatus | null): LocationSituation =>
  status && status in CHARGE_STATUS
    ? CHARGE_STATUS[status as Exclude<LocationChargeStatus, ''>]
    : CHARGE_STATUS.none;

/** "1978-03-12" → "12/03/1978". */
export const formatIsoDateBR = (iso?: string | null): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? '');
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
};

export const LINK_VALIDITY_DAYS = 7;

export const MARITAL_STATUS_OPTIONS = [
  'Solteiro(a)',
  'Casado(a)',
  'União Estável',
  'Divorciado(a)',
  'Viúvo(a)',
];

export interface ProposalFields {
  plan_percentage?: LocationPlanPercentage | null;
  plan_addon?: LocationPlanAddon | null;
  fee_option?: LocationFeeOption | null;
  send_channel?: LocationSendChannel | null;
}

/** Plano + taxa + canal; no 22% o adicional faz parte da escolha. */
export const isProposalComplete = (p: ProposalFields): boolean => {
  if (!p.plan_percentage || !p.fee_option || !p.send_channel) return false;
  if (p.plan_percentage === 22) return Boolean(p.plan_addon);
  return true;
};

export const planLabel = (p: ProposalFields): string => {
  if (!p.plan_percentage) return '';
  const base = PLANS[p.plan_percentage]?.label ?? `Plano ${p.plan_percentage}%`;
  const addon = p.plan_percentage === 22 && p.plan_addon ? PLAN_ADDONS[p.plan_addon] : '';
  return addon ? `${base} + ${addon}` : base;
};

export const feeLabel = (fee?: LocationFeeOption | null): string =>
  fee ? (FEES[fee]?.label ?? '') : '';

export const formatCurrencyBRL = (value: number): string =>
  `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

/** Destaque do card da taxa: a parcela quando é parcelado fixo ("3x de R$ 400"), senão o total. */
export const feeHighlight = (fee: LocationFeeDef): string =>
  fee.installments
    ? `${fee.installments.count}x de ${formatCurrencyBRL(fee.installments.value)}`
    : formatCurrencyBRL(fee.value);

/* ── Tipos de indicação (landing e etapa 1 do wizard) ───────────────── */

export type IndicationTypeIconKey =
  | 'home'
  | 'trending'
  | 'terrain'
  | 'apartment'
  | 'weekend'
  | 'handshake';

export interface IndicationTypeCard {
  key: 'locacao' | 'spot' | 'terreno' | 'construtora' | 'decor' | 'franquia';
  /** Chave do ícone do tipo; nada de emoji nas telas. */
  icon: IndicationTypeIconKey;
  title: string;
  description: string;
  gain: [string, string];
  /** Rota do formulário atual (`/parceiros/indicar/formulario/<formType>`). */
  formType: EnumType;
}

/**
 * A régua da comissão do parceiro, que é a mesma nos dois caminhos da porta e não tem nada de
 * novo: 2% ao mês das reservas, vitalícios para o parceiro Premium e limitados a 12 meses da
 * ativação para os demais. Quem decide é o tier, e o back escreve isso no card do Pipedrive na
 * criação da indicação.
 *
 * No detalhe de uma indicação a régua vem pronta do back (`commission.rate_label` e
 * `commission.rule_label`). A porta não tem indicação nenhuma carregada ainda, então repete o
 * texto a partir do tier do usuário; por isso as strings vivem aqui e não soltas na tela.
 */
export const COMMISSION_RATE_LABEL = '2% ao mês das reservas';

/** Valores de `partnership_tier` que recebem enquanto o imóvel ficar com a Seazone. */
const LIFETIME_TIERS = ['premium', 'lifetime premium'];

export const isLifetimeCommissionTier = (tier?: string | null): boolean =>
  LIFETIME_TIERS.includes((tier ?? '').trim().toLowerCase());

export const commissionRuleLabel = (tier?: string | null): string =>
  isLifetimeCommissionTier(tier)
    ? 'enquanto o imóvel ficar com a Seazone'
    : 'por 12 meses a partir da ativação do imóvel';

export const INDICATION_TYPES: IndicationTypeCard[] = [
  {
    key: 'locacao',
    icon: 'home',
    title: 'Imóvel para locação',
    description: 'Proprietário que quer colocar o imóvel na gestão',
    gain: ['2% a.m.', 'das reservas'],
    formType: EnumType.location,
  },
  {
    key: 'spot',
    icon: 'trending',
    title: 'Spot',
    description: 'Investidor que quer comprar uma unidade',
    gain: ['4%', 'da cota'],
    formType: EnumType.spot,
  },
  {
    key: 'terreno',
    icon: 'terrain',
    title: 'Terreno',
    description: 'Terreno com potencial construtivo',
    gain: ['até 6%', 'do valor'],
    formType: EnumType.terrain,
  },
  {
    key: 'construtora',
    icon: 'apartment',
    title: 'Construtora',
    description: 'Incorporadora com empreendimento',
    gain: ['R$ 500', 'por contrato'],
    formType: EnumType.builder,
  },
  {
    key: 'decor',
    icon: 'weekend',
    title: 'Decor',
    description: 'Quem precisa mobiliar ou reformar',
    gain: ['R$ 500', 'por indicação'],
    formType: EnumType.decor,
  },
  {
    key: 'franquia',
    icon: 'handshake',
    title: 'Franquia',
    description: 'Alguém com perfil para abrir uma franquia',
    gain: ['R$ 500', 'por indicação'],
    formType: EnumType.franchise,
  },
];

export const findIndicationType = (key?: string | null) =>
  INDICATION_TYPES.find((t) => t.key === key);

/* ── Datas ──────────────────────────────────────────────────────────── */

export const formatDateBR = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

export const formatDateTimeBR = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/* ── Descrição do imóvel ────────────────────────────────────────────── */

const PROPERTY_TYPE_LABELS: Record<string, string> = typeOptions.reduce<Record<string, string>>(
  (acc, opt) => ({ ...acc, [String(opt.value)]: String(opt.valueLabel) }),
  {},
);

export const propertyTypeLabel = (type?: string | null): string =>
  type ? (PROPERTY_TYPE_LABELS[type] ?? type) : '';

type PropertyLike = Pick<
  ILocationIndication,
  | 'property_type'
  | 'property_neighborhood'
  | 'property_city'
  | 'property_state'
  | 'property_code'
  | 'address'
  | 'owner_phone'
  | 'owner_email'
>;

/**
 * "Apartamento · Meia Praia, Itapema/SC · código PBL0301".
 * Indicação mínima (só contato) mostra telefone e e-mail no lugar do imóvel.
 */
export const describeProperty = (item: PropertyLike): string => {
  const type = propertyTypeLabel(item.property_type);
  const cityState = [item.property_city, item.property_state].filter(Boolean).join('/');
  const place = [item.property_neighborhood, cityState].filter(Boolean).join(', ');
  const code = item.property_code ? `código ${item.property_code}` : '';
  const parts = [type, place, code].filter(Boolean);
  if (parts.length > 0) return parts.join(' · ');
  if (item.address) return item.address;
  return [item.owner_phone, item.owner_email].filter(Boolean).join(' · ');
};

export const shortAddress = (item: Partial<ILocationIndication>): string => {
  const street = [item.property_street, item.property_number].filter(Boolean).join(', ');
  const cityState = [item.property_city, item.property_state].filter(Boolean).join('/');
  const place = [item.property_neighborhood, cityState].filter(Boolean).join(', ');
  return [street, place].filter(Boolean).join(' · ');
};

/* ── Fallbacks quando o back não manda track/situation/actions ─────── */

const TERMINAL_BAD = ['Lost', 'Canceled'];

const normalizeStatus = (raw?: string | null): string => {
  if (!raw) return 'In_Progress';
  return raw
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join('_');
};

/**
 * Deriva a esteira dos marcos espelhados do motor. É o único lugar que traduz
 * status em etapa (substitui os três `statusToStepIndex` que existiam).
 */
export const trackFromMarks = (item: Partial<ILocationIndication>): LocationTrackItem[] => {
  const status = normalizeStatus(item.status);
  const failed = TERMINAL_BAD.includes(status);
  const won = status === 'Won';

  const done: Record<LocationTrackKey, boolean> = {
    indicated: true,
    sent: Boolean(item.signature_sent_at),
    opened: Boolean(item.link_opened_at),
    paid: Boolean(item.paid_at),
    signed: Boolean(item.signed_client_at),
    won,
  };
  const at: Record<LocationTrackKey, string | null | undefined> = {
    indicated: item.created_at,
    sent: item.signature_sent_at,
    opened: item.link_opened_at,
    paid: item.paid_at,
    signed: item.signed_client_at,
    won: won ? item.updated_at : null,
  };

  let currentAssigned = false;
  return TRACK_STEPS.map(({ key, label }) => {
    let state: LocationTrackState;
    if (done[key]) {
      state = 'done';
    } else if (!currentAssigned && !won && key !== 'won') {
      currentAssigned = true;
      if (failed) state = 'error';
      else if (key === 'signed' && item.signed_client_at && !item.signed_seazone_at) state = 'warn';
      else state = 'current';
    } else {
      state = 'pending';
    }
    return { key, label, state, at: done[key] ? (at[key] ?? null) : null, available: done[key] };
  });
};

export const situationFromStatus = (item: Partial<ILocationIndication>): LocationSituation => {
  const status = normalizeStatus(item.status);
  if (status === 'Won') return { text: 'Ganho · contrato fechado', tone: 'ok' };
  if (status === 'Lost') return { text: 'Perdido', tone: 'err' };
  if (status === 'Canceled') return { text: 'Cancelada', tone: 'gray' };
  if (item.coverage_status === 'outside') {
    return { text: 'Fora da área de cobertura · a Seazone avalia', tone: 'err' };
  }
  if (!item.contract_url) {
    return {
      text: item.property_street
        ? 'Indicada · falta montar a proposta'
        : 'Indicada · falta completar',
      tone: 'warn',
    };
  }
  if (item.paid_at && !item.signed_seazone_at) return { text: 'Pagou · falta assinar', tone: 'ok' };
  if (item.link_opened_at) return { text: 'Abriu o link · falta pagar', tone: '' };
  return { text: 'Link enviado', tone: '' };
};

const NO_ACTIONS: LocationActions = {
  continue: false,
  resend: false,
  renew: false,
  transfer: false,
  cancel: false,
  in_flight: false,
};

export const resolveTrack = (item: Partial<ILocationIndication>): LocationTrackItem[] =>
  Array.isArray(item.track) && item.track.length === TRACK_STEPS.length
    ? item.track
    : trackFromMarks(item);

export const resolveSituation = (item: Partial<ILocationIndication>): LocationSituation =>
  item.situation && item.situation.text ? item.situation : situationFromStatus(item);

export const resolveActions = (item: Partial<ILocationIndication>): LocationActions => ({
  ...NO_ACTIONS,
  ...(item.actions ?? {}),
});

/** Rascunho: `In_Progress` sem contrato gerado. É o que a landing e a lista chamam de "Continuar". */
/**
 * Fechamento começado e não terminado. A indicação pura não entra: ela não tem
 * o que continuar, quem conduz dali é a Seazone.
 */
export const isDraft = (item: Partial<ILocationIndication>): boolean =>
  normalizeStatus(item.status) === 'In_Progress' && !item.contract_url && !isIndicationOnly(item);

/* ── Erros da API ───────────────────────────────────────────────────── */

export interface LocationApiError {
  status: number;
  code: string;
  message: string;
  missing: string[];
  /**
   * Erro de validação do DRF por campo (`{"property_google_maps_link": ["..."]}`),
   * já achatado em uma mensagem por campo. É o formato do 400 do rascunho, que
   * não tem `error`/`detail` nenhum — sem ler isto o erro some.
   */
  fieldErrors: Record<string, string>;
}

interface ErrorLike {
  statusCode?: number;
  status?: number;
  message?: string;
  detail?: unknown;
  response?: { status?: number; data?: unknown };
}

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

/** Chaves do corpo que não são nome de campo, e sim o envelope de erro da API. */
const CHAVES_DE_ENVELOPE = [
  'error',
  'error_code',
  'detail',
  'details',
  'message',
  'non_field_errors',
];

/** `{"campo": ["msg1", "msg2"]}` → `{campo: "msg1 msg2"}`. */
const readFieldErrors = (body: Record<string, unknown>): Record<string, string> => {
  const campos: Record<string, string> = {};
  Object.entries(body).forEach(([campo, valor]) => {
    if (CHAVES_DE_ENVELOPE.includes(campo)) return;
    const texto = Array.isArray(valor)
      ? valor.filter((v) => typeof v === 'string').join(' ')
      : valor;
    if (typeof texto === 'string' && texto.trim()) campos[campo] = texto.trim();
  });
  return campos;
};

/**
 * Lê o erro no formato do interceptor do `services/request` (`ErrorResponse`
 * com `statusCode` e `detail` = corpo da resposta) e também no formato cru do
 * axios, para o caso de alguém chamar o serviço sem passar por ele.
 */
export const readLocationApiError = (error: unknown): LocationApiError => {
  const err = asRecord(error) as ErrorLike;
  const body = asRecord(err.detail ?? err.response?.data);
  const details = asRecord(body.details);
  const status = Number(err.statusCode ?? err.status ?? err.response?.status ?? 0);
  const code = String(body.error_code ?? '');
  const missing = Array.isArray(details.missing) ? details.missing.map(String) : [];
  const fieldErrors = readFieldErrors(body);
  const naoNomeado = Array.isArray(body.non_field_errors)
    ? body.non_field_errors.filter((v) => typeof v === 'string').join(' ')
    : undefined;
  const rawMessage = body.error ?? body.detail ?? body.message ?? naoNomeado ?? err.message;
  const message = typeof rawMessage === 'string' ? rawMessage : '';
  return { status, code, message, missing, fieldErrors };
};

const MISSING_FIELD_LABELS: Record<string, string> = {
  plan_percentage: 'plano',
  plan_addon: 'adicional do 22%',
  fee_option: 'taxa de implantação',
  send_channel: 'canal de envio',
  owner_document: 'CPF do proprietário',
  owner_marital_status: 'estado civil',
  owner_profession: 'profissão',
  owner_birth_date: 'data de nascimento',
  owner_nationality: 'nacionalidade',
  owner_address: 'endereço residencial do proprietário',
  owner_address_cep: 'CEP do endereço residencial',
  signature_channel: 'canal da assinatura',
  owner_name: 'nome do proprietário',
  owner_phone: 'telefone do proprietário',
  owner_email: 'e-mail do proprietário',
  property_cep: 'CEP',
  property_street: 'logradouro',
  property_number: 'número',
  property_neighborhood: 'bairro',
  property_city: 'cidade',
  property_state: 'estado',
  property_type: 'tipo de propriedade',
  property_google_maps_link: 'link do Google Maps',
  property_complement: 'complemento',
  owner_phone_2: 'segundo telefone',
  owner_address_street: 'logradouro do endereço residencial',
  owner_address_number: 'número do endereço residencial',
  owner_address_neighborhood: 'bairro do endereço residencial',
  owner_address_city: 'cidade do endereço residencial',
  owner_address_state: 'estado do endereço residencial',
  comment: 'observações',
};

export const locationFieldLabel = (field: string): string => MISSING_FIELD_LABELS[field] ?? field;

/**
 * Erro do rascunho (`PATCH /{id}/`) em uma frase. O 400 do DRF vem por campo e
 * sem envelope, então sem isto o parceiro só via a etapa não avançar (bug do
 * Ambrosi, 31/08): o link do Google recusado não dizia nada.
 */
export const translateDraftError = (error: unknown): string => {
  const { status, code, message, fieldErrors } = readLocationApiError(error);
  if (status === 409 || code === 'LOCATION_INDICATION_LOCKED') {
    return 'Este contrato já foi gerado. O rascunho não pode mais ser alterado.';
  }
  const campos = Object.entries(fieldErrors);
  if (campos.length) {
    return campos.map(([campo, msg]) => `${locationFieldLabel(campo)}: ${msg}`).join(' ');
  }
  if (status === 502 || status === 503 || status === 504) {
    return 'Não conseguimos salvar agora. Tente de novo em instantes.';
  }
  return (
    message || 'Não foi possível salvar o que você preencheu. Confira os campos e tente de novo.'
  );
};

export const translateGenerateContractError = (error: unknown): string => {
  const { status, code, message, missing } = readLocationApiError(error);
  switch (code) {
    case 'LOCATION_INDICATION_OUT_OF_COVERAGE':
      return 'O endereço ficou fora da área de atuação. A indicação continua valendo, e a Seazone avalia o caso.';
    case 'LOCATION_INDICATION_PROPOSAL_INCOMPLETE': {
      const campos = missing.map((f) => MISSING_FIELD_LABELS[f] ?? f).join(', ');
      return campos
        ? `Falta completar a proposta: ${campos}.`
        : 'Falta completar a proposta antes de gerar o contrato.';
    }
    case 'LOCATION_INDICATION_PARTNER_INCOMPLETE':
      return 'Seu cadastro de parceiro está sem CPF ou telefone. Complete-o em Editar Organização antes de gerar o contrato.';
    case 'LOCATION_INDICATION_LOCKED':
      return 'Este contrato já foi gerado. Acompanhe a indicação em Minhas indicações.';
    default:
      break;
  }
  if (status === 409)
    return 'Este contrato já foi gerado. Acompanhe a indicação em Minhas indicações.';
  if (status === 502 || status === 503 || status === 504) {
    return 'O CRM ou o motor de contratos não respondeu. Tente de novo em instantes.';
  }
  return message || 'Não foi possível gerar o contrato. Tente novamente.';
};
